import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, ErrorMessage } from "formik";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import { serviceValidationSchema } from "../../../config/Validate";
import {
  getAllDetails,
  postAllData,
  updateData,
} from "../../../service/ProductService";
import {
  ADD_SERVICE,
  EDIT_SERVICE,
  GET_EQUIPMENT_LIST,
  GET_SERVICE_BY_ID,
} from "../../../config/ApiConstant";
import { ServiceType } from "../../../appconfig/interfaces";
import AppBreadcrumb from "../../../components/CustomeBreadcrumb/AppBreadcrumb";
import Loader from "../../../components/Loadeer/Loader";
import { Dropdown } from "primereact/dropdown";

const AddEditService = () => {
  // useparams and usenavigate
  const { id }: any = useParams();
  const navigate = useNavigate();

  //   decode id
  const service_id = id && atob(id);

  //   toast ref
  const toast = useRef<any>(null);

  //   page states
  const [selectedService, setSelectedService] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [oldPhoto, setOldPhoto] = useState<string | null>(null);
  const [pageLoader, setPageLoader] = useState<boolean>(false);

  // set equipment state
  const [equipmentList, setEquipmentList] = useState<any[]>([]);

  //   if id has then isedit mode set in to boolean
  const isEditMode = Boolean(id);

  // get serviec data
  const getServiceMasterData = async () => {
    if (!isEditMode && !service_id) return;
    try {
      setPageLoader(true);
      const response = await getAllDetails(
        `${GET_SERVICE_BY_ID}/${service_id}`
      );
      if (response) {
        setSelectedService(response);
        setOldPhoto(response.photo);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setPageLoader(false);
    }
  };

  //Fetch Equipment list for dropdown
  const getEquipmentList = async () => {
    try {
      setPageLoader(true);
      const response = await getAllDetails(
        GET_EQUIPMENT_LIST + "?type=dropdown"
      );
      setEquipmentList(response || []);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setPageLoader(false);
    }
  };

  // useefect
  useEffect(() => {
    getServiceMasterData();
    getEquipmentList();
  }, [service_id, isEditMode]);

  // innitial value set
  const initialValues: ServiceType = {
    service_name: selectedService?.service_name || "",
    service_name_hi: selectedService?.service_name_hi || "",
    service_name_gu: selectedService?.service_name_gu || "",
    price: selectedService?.price || "",
    operator_price: selectedService?.operator_price || "",
    photo: selectedService?.photo || "",
    description: selectedService?.description || "",
    price_on: selectedService?.price_on || "",
    type_of_equipment:
      isEditMode && selectedService?.type_of_equipment
        ? selectedService.type_of_equipment.trim()
        : "",
  };

  // Submit handler
  const onSubmit = async (values: any, { resetForm }: any) => {
    try {
      setLoading(true);

      // set formdata
      const formData = new FormData();
      formData.append("service_name", values.service_name);
      formData.append("price", Number(values.price).toString());
      formData.append("description", values.description);
      formData.append("price_on", values.price_on);
      formData.append("operator_price", Number(values.operator_price).toString());
      if (values.type_of_equipment) {
        formData.append("type_of_equipment", values.type_of_equipment);
      }

      // append extra fields if filled
      if (values.service_name_hi) {
        formData.append("service_name_hi", values.service_name_hi);
      }
      if (values.service_name_gu) {
        formData.append("service_name_gu", values.service_name_gu);
      }

      if (values.photo instanceof File) {
        formData.append("photo", values.photo);
      }

      const response = isEditMode
        ? await updateData(EDIT_SERVICE, service_id, formData)
        : await postAllData(ADD_SERVICE, formData);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        resetForm();
        setTimeout(() => {
          navigate("/master/services");
        }, 500);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (pageLoader) {
    return <Loader />;
  }

  return (
    <>
      {/* Breadcrumb */}
      <AppBreadcrumb
        currentMenu="Services"
        pageName={isEditMode ? "Edit Service" : "Add Service"}
        pageTitle={isEditMode ? "Edit Service" : "Add Service"}
        goToPage={true}
      />
      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toast ref={toast} />

            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={serviceValidationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
              }) => (
                <Form className="p-fluid">
                  <div className="grid formgrid">
                    {/* Service Name (English - required) */}
                    <div className="field col-12 md:col-6">
                      <label>Service Name (English)*</label>
                      <InputText
                        name="service_name"
                        autoComplete="off"
                        value={values.service_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={
                          touched.service_name && errors.service_name
                            ? "p-invalid"
                            : ""
                        }
                      />
                      <small className="p-error">
                        <ErrorMessage name="service_name" />
                      </small>
                    </div>

                    {/* Service Name (Hindi - optional) */}
                    <div className="field col-12 md:col-6">
                      <label>Service Name (Hindi)</label>
                      <InputText
                        name="service_name_hi"
                        autoComplete="off"
                        value={values.service_name_hi}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Service Name (Gujarati - optional) */}
                    <div className="field col-12 md:col-6">
                      <label>Service Name (Gujarati)</label>
                      <InputText
                        name="service_name_gu"
                        autoComplete="off"
                        value={values.service_name_gu}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Price */}
                    <div className="field col-12 md:col-6">
                      <label>Price *</label>
                      <div className="flex-1 input-effect-div p-inputgroup">
                        <span className="font-bold p-inputgroup-addon">
                          &#x20B9;
                        </span>
                        <InputText
                          keyfilter="int"
                          id="price"
                          name="price"
                          onBlur={handleBlur}
                          value={values.price}
                          onChange={handleChange}
                          autoComplete="off"
                          className={
                            touched.price && errors.price ? "p-invalid" : ""
                          }
                        />
                      </div>
                      <small className="p-error">
                        <ErrorMessage name="price" />
                      </small>
                    </div>

                    {/* Price On */}
                    <div className="field col-12 md:col-6">
                      <label>Price On *</label>
                      <InputText
                        name="price_on"
                        value={values.price_on}
                        onChange={handleChange}
                        autoComplete="off"
                        onBlur={handleBlur}
                        className={
                          touched.price_on && errors.price_on ? "p-invalid" : ""
                        }
                      />
                      <small className="p-error">
                        <ErrorMessage name="price_on" />
                      </small>
                    </div>

                    {/* Operator Price */}
                    <div className="field col-12 md:col-6">
                      <label>Operator Price *</label>
                      <div className="flex-1 input-effect-div p-inputgroup">
                        <span className="font-bold p-inputgroup-addon">
                          &#x20B9;
                        </span>
                        <InputText
                          keyfilter="int"
                          id="operator_price"
                          name="operator_price"
                          onBlur={handleBlur}
                          value={values.operator_price}
                          onChange={handleChange}
                          autoComplete="off"
                          className={
                            touched.operator_price && errors.operator_price
                              ? "p-invalid"
                              : ""
                          }
                        />
                      </div>
                      <small className="p-error">
                        <ErrorMessage name="operator_price" />
                      </small>
                    </div>

                    {/* Description */}
                    <div className="field col-12 md:col-6">
                      <label>Description *</label>
                      <InputTextarea
                        rows={3}
                        name="description"
                        autoComplete="off"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={
                          touched.description && errors.description
                            ? "p-invalid"
                            : ""
                        }
                      />
                      <small className="p-error">
                        <ErrorMessage name="description" />
                      </small>
                    </div>

                    {/*Equipment field */}
                    {/* Equipment field (Dropdown version) */}
                    <div className="field col-12 md:col-6">
                      <label htmlFor="type_of_equipment">
                        Type of Equipment *
                      </label>
                      <Dropdown
                        id="type_of_equipment"
                        name="type_of_equipment"
                        value={values.type_of_equipment}
                        options={equipmentList.map((eq) => ({
                          label: eq.equipment_name,
                          value: eq.equipment_name,
                        }))}
                        onChange={(e) =>
                          setFieldValue("type_of_equipment", e.value)
                        }
                        optionLabel="label"
                        placeholder="Select Equipment"
                        className={
                          touched.type_of_equipment && errors.type_of_equipment
                            ? "p-invalid"
                            : ""
                        }
                      />
                      <small className="p-error">
                        <ErrorMessage name="type_of_equipment" />
                      </small>
                    </div>

                    {/* service image feild */}
                    <div className="grid px-3 field align-items-start md:gap-2 justify-content-center md:justify-content-between col-12">
                      <div className="grid block mt-2 md:mt-0 col-12 md:col-6">
                        <label htmlFor="photo">Service Image *</label>
                        <div className="upload-box">
                          <i
                            className="pi pi-upload"
                            style={{
                              fontSize: "1.2rem",
                              marginRight: "0.5rem",
                            }}
                          ></i>
                          <span>
                            {isEditMode ? "Change Image" : "Upload Image"}
                          </span>
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/jpg"
                            className="file-input"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setFieldValue("photo", file);
                                setImagePreview(URL.createObjectURL(file));
                                if (isEditMode) setOldPhoto(null);
                              }
                            }}
                          />
                        </div>
                        <small className="p-error">
                          <ErrorMessage name="photo" />
                        </small>
                      </div>
                      <div className="grid block mt-3 md:mt-0 col-12 md:col-6">
                        {/* Old photo in edit mode */}
                        {isEditMode && oldPhoto && !imagePreview && (
                          <>
                            <label>Current Image</label>
                            <div className="file-preview">
                              <div className="file-info">
                                <a
                                  href={oldPhoto}
                                  target="_blank"
                                  style={{ display: "block" }}
                                  rel="noopener noreferrer"
                                >
                                  {oldPhoto.split("/").pop()}
                                </a>
                              </div>
                              <i
                                className="pi pi-trash file-delete"
                                onClick={() => {
                                  setOldPhoto(null);
                                  setFieldValue("photo", "");
                                }}
                                title="Delete"
                              ></i>
                            </div>
                          </>
                        )}

                        {/* Preview new uploaded file */}
                        {imagePreview && (
                          <div className="file-preview">
                            <div className="file-info">
                              <a
                                href={imagePreview}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: "block" }}
                              >
                                {values.photo instanceof File
                                  ? values.photo.name
                                  : ""}
                              </a>
                              <span className="file-details">
                                {values.photo instanceof File
                                  ? `${values.photo.type} ♦ ${(
                                      values.photo.size / 1024
                                    ).toFixed(2)} KB`
                                  : ""}
                              </span>
                            </div>
                            <i
                              className="pi pi-trash file-delete"
                              onClick={() => {
                                setFieldValue("photo", "");
                                setImagePreview(null);
                              }}
                              title="Delete"
                            ></i>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4 justify-content-end">
                    <Button
                      label="Cancel"
                      icon="pi pi-times"
                      className="p-button-secondary w-fit"
                      onClick={() => navigate("/master/services")}
                    />
                    <Button
                      label={isEditMode ? "Update" : "Save"}
                      icon="pi pi-check"
                      className="w-fit"
                      type="submit"
                      loading={loading}
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEditService;
